import { Capacitor } from '@capacitor/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useAxios } from '@healthblocks-io/core'
import { i18n } from '@lingui/core'
import { useEffect, useMemo, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { useMediaQuery } from 'usehooks-ts'

import { BottomSheet } from '@/@bothrs-ui/web/components/bottom-sheet/BottomSheet'
import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Markdown } from '@/@bothrs-ui/web/components/markdown/Markdown'
import { Modal } from '@/@bothrs-ui/web/components/modal/Modal'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { breakpoints } from '@/config/layout'
import { globalDefinitions } from '@/defined-translations'
import { PdfPreview } from '@/modules/documents/pages/pdf-preview/PdfPreview'
import { formatLocale } from '@/modules/shared/helpers/locale'
import { useToast } from '@/modules/shared/hooks/useToast'
import { RelatedTriggers } from '@/modules/timeline/components/related-triggers/RelatedTriggers'
import { openMail } from '@/utils/open-mail'
import { useTaskStatusMetaData } from '../../hooks/useTaskStatusMetaData'
import { type FileUploadRequestTask, TaskStatus } from '../../types/task.types'
import { formatConstraintHint } from '../../utils/file-upload-selection.utils'
import { TaskDetailLayout } from '../detail-layout/TaskDetailLayout'
import { FileUploadSelection } from './FileUploadSelection'

type UploadedFile = {
  id: string
  url: string | null
  mimetype: string | null
  filename: string | null
}

type Props = {
  task: FileUploadRequestTask
  refetch: () => void
}

export const FileUploadRequestDetail = ({ task, refetch }: Props) => {
  const theme = useTheme()
  const axios = useAxios()
  const { showErrorToast } = useToast()
  const { daysTillExpiry } = useTaskStatusMetaData(task)
  const isMobile = useMediaQuery(breakpoints.toS) || Capacitor.isNativePlatform()
  const [showUpload, setShowUpload] = useState(false)
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])
  const [selectedFile, setSelectedFile] = useState<UploadedFile | null>(null)
  const [selectedPdf, setSelectedPdf] = useState<UploadedFile | null>(null)

  const isCompleted = task.status === TaskStatus.Completed
  const fileIds = task.meta?.fileIds

  useEffect(() => {
    if (!isCompleted || !fileIds?.length) return

    let cancelled = false

    Promise.all(
      fileIds.map((id) =>
        axios
          .get<UploadedFile>(`/file-upload/${id}`)
          .then((response) => response.data)
          .catch(() => ({ id, url: null, mimetype: null, filename: null })),
      ),
    ).then((files) => {
      if (!cancelled) setUploadedFiles(files)
    })

    return () => {
      cancelled = true
    }
  }, [isCompleted, fileIds?.join(',')])

  const statusDisplay = useMemo((): string => {
    if (isCompleted && task.completedAt) {
      return i18n._(globalDefinitions.task.FileUploadRequest.completed, {
        date: formatLocale(task.completedAt, 'dd/MM/yyyy'),
        time: formatLocale(task.completedAt, 'HH:mm'),
      })
    }

    if (daysTillExpiry === 0) {
      return i18n._(globalDefinitions.task.FileUploadRequest.expires_today)
    }

    if (daysTillExpiry === 1) {
      return i18n._(globalDefinitions.task.FileUploadRequest.expires_in_one_day)
    }

    if (daysTillExpiry !== undefined && daysTillExpiry > 1) {
      return i18n._(globalDefinitions.task.FileUploadRequest.expires_in_x_days, {
        count: daysTillExpiry,
      })
    }

    return i18n._(globalDefinitions.task.FileUploadRequest.pending, {
      date: formatLocale(task.plannedAt, 'dd/MM/yyyy'),
    })
  }, [isCompleted, daysTillExpiry, task])

  const handleDeleteRequest = () => {
    const fileList = task.meta.fileIds?.join('\n') ?? ''
    openMail({
      to: ['info@mariamiddelares.be'],
      subject: 'Verzoek tot verwijdering van bestanden',
      body: [
        'Geachte,',
        '',
        'Ik wens de volgende bestanden uit mijn dossier te laten verwijderen.',
        '',
        `Activiteit ID: ${task.careplanActivityId}`,
        `Bestand-ID's:\n${fileList}`,
        '',
        'Met vriendelijke groeten,',
      ].join('\n'),
      onError: (message) => showErrorToast({ message }),
    })
  }

  const constraintHint = formatConstraintHint(task.meta?.allowedTypes ?? []) || null

  const selectionContent = (
    <FileUploadSelection
      task={task}
      onClose={() => setShowUpload(false)}
      onSuccess={() => {
        setShowUpload(false)
        refetch()
      }}
    />
  )

  const handleFileClick = (file: UploadedFile) => {
    if (!file.url) return
    if (file.mimetype?.startsWith('image/')) {
      setSelectedFile(file)
    } else if (file.mimetype === 'application/pdf') {
      setSelectedPdf(file)
    }
  }

  return (
    <>
      <TaskDetailLayout>
        <TaskDetailLayout.Header task={task} statusLabel={statusDisplay} />

        <TaskDetailLayout.Content>
          <FlexColumn gap={theme.UI.SpacingPx.Space6}>
            <Markdown
              content={task.meta.description}
              typography='BodyMediumRegular'
              color={theme.Colors.Neutrals.Darkest}
            />

            {task.trigger && <RelatedTriggers trigger={task.trigger} />}

            {isCompleted && uploadedFiles.length > 0 && (
              <FlexColumn gap={theme.UI.SpacingPx.Space3}>
                <H3 color={theme.Colors.Neutrals.Darker}>
                  {i18n._(globalDefinitions.task.FileUploadRequest.uploaded_files)}
                </H3>
                <StFileGrid>
                  {uploadedFiles.map((file) => {
                    const unavailable = !file.url && !file.filename && !file.mimetype
                    return (
                      <StFileCell key={file.id}>
                        <StFileTile
                          clickable={!!file.url}
                          unavailable={unavailable}
                          onClick={() => handleFileClick(file)}
                        >
                          {file.url && file.mimetype?.startsWith('image/') ? (
                            <StTileImage src={file.url} alt={file.filename ?? ''} />
                          ) : (
                            <FontAwesomeIcon
                              icon={file.mimetype === 'application/pdf' ? ['fal', 'file-pdf'] : ['fal', 'file']}
                              size='2x'
                              color={unavailable ? theme.Colors.Neutrals.Light : theme.Colors.Neutrals.Darker}
                            />
                          )}
                        </StFileTile>
                        {unavailable ? (
                          <Span
                            type='BodySmallRegular'
                            color={theme.Colors.Neutrals.Dark}
                            style={{ textAlign: 'center' }}
                          >
                            {i18n._(globalDefinitions.task.FileUploadRequest.file_unavailable)}
                          </Span>
                        ) : (
                          file.filename && (
                            <Span
                              type='BodySmallRegular'
                              color={theme.Colors.Neutrals.Darkest}
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {file.filename}
                            </Span>
                          )
                        )}
                      </StFileCell>
                    )
                  })}
                </StFileGrid>
                <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darker}>
                  {i18n._(globalDefinitions.task.FileUploadRequest.delete_files_note)}{' '}
                  <StContactLink onClick={handleDeleteRequest}>
                    {i18n._(globalDefinitions.task.FileUploadRequest.delete_files_contact)}
                  </StContactLink>
                </Span>
              </FlexColumn>
            )}
          </FlexColumn>
        </TaskDetailLayout.Content>

        {!isCompleted && (
          <TaskDetailLayout.Footer>
            <FlexColumn gap={theme.UI.SpacingPx.Space2}>
              {constraintHint && (
                <Span type='BodySmallRegular' color={theme.Colors.Neutrals.Darker} style={{ textAlign: 'center' }}>
                  {constraintHint}
                </Span>
              )}
              <Button variant='Primary' prefixIcon={['fal', 'arrow-up-from-line']} onClick={() => setShowUpload(true)}>
                {i18n._(globalDefinitions.task.FileUploadRequest.upload_cta)}
              </Button>
            </FlexColumn>
          </TaskDetailLayout.Footer>
        )}
      </TaskDetailLayout>

      <BottomSheet
        steps={[95, 100]}
        visible={isMobile && showUpload}
        onClose={() => setShowUpload(false)}
        closeCtaVisible={false}
      >
        <StBottomSheetInner>{selectionContent}</StBottomSheetInner>
      </BottomSheet>

      <Modal isVisible={!isMobile && showUpload} onClose={() => setShowUpload(false)} maxHeight='90vh'>
        {selectionContent}
      </Modal>

      {/* Full-screen image viewer */}
      <Modal
        isVisible={!!selectedFile}
        onClose={() => setSelectedFile(null)}
        fullScreen
        noPadding
        backdropColor='rgba(0, 0, 0, 0.85)'
      >
        <StFullScreenContent>
          {selectedFile?.url && <StFullScreenImage src={selectedFile.url} alt={selectedFile.filename ?? ''} />}
          <StCloseButton
            type='button'
            onClick={() => setSelectedFile(null)}
            aria-label={i18n._(globalDefinitions.close)}
          >
            <FontAwesomeIcon icon={['fal', 'times']} color='#fff' size='lg' />
          </StCloseButton>
        </StFullScreenContent>
      </Modal>

      {selectedPdf?.url && (
        <PdfPreview
          filename={selectedPdf.filename ?? ''}
          preview={selectedPdf.url}
          onClose={() => setSelectedPdf(null)}
        />
      )}
    </>
  )
}

const StBottomSheetInner = styled.div`
  height: 100%;
  padding: ${({ theme }) => `0 ${theme.UI.SpacingPx.Space4} ${theme.UI.SpacingPx.Space6}`};
`

const StFileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  align-items: start;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space3};

  @media ${breakpoints.toS} {
    grid-template-columns: repeat(2, 1fr);
  }
`

const StFileCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space1};
  min-width: 0;
`

const StFileTile = styled.div<{ clickable: boolean; unavailable?: boolean }>`
  aspect-ratio: 1;
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Large}px;
  overflow: hidden;
  background-color: ${({ theme, unavailable }) =>
    unavailable ? theme.Colors.Neutrals.Lightest : theme.Colors.Neutrals.Lighter};
  border: ${({ theme, unavailable }) => (unavailable ? `2px dashed ${theme.Colors.Neutrals.Light}` : 'none')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
`

const StTileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`

const StFullScreenContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`

const StCloseButton = styled.button`
  position: absolute;
  top: calc(
    var(--safe-area-inset-top, 0px) +
      ${({ theme }) => theme.UI.SpacingPx.Space4}
  );
  right: calc(
    var(--safe-area-inset-right, 0px) +
      ${({ theme }) => theme.UI.SpacingPx.Space4}
  );
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background-color: rgba(0, 0, 0, 0.5);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`

const StFullScreenImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`

const StContactLink = styled.a`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.Colors.Theme.Primary};
  text-decoration: underline;
  font: inherit;
`
